import React from "react";
import ExerciseList from "./ExerciseList";
import CardioList from "./CardioList";
import SportsList from "./SportsList";

const frequencyOptions = Array.from({ length: 8 }, (_, i) => i);

export default function CalculatorForm({
  basics,
  setBasics,
  lifestyle,
  setLifestyle,
  useWorkoutProfile,
  setUseWorkoutProfile,
  derivedTraining,
  strengthWorkouts,
  setStrengthWorkouts,
  strengthUnit,
  onStrengthUnitChange,
  cardioSessions,
  setCardioSessions,
  sportsSessions,
  setSportsSessions,
  validation,
  onHeightUnitChange,
  onWeightUnitChange,
}) {
  const updateBasics = (updates) =>
    setBasics((prev) => ({ ...prev, ...updates }));

  const updateLifestyle = (updates) =>
    setLifestyle((prev) => ({ ...prev, ...updates }));

  const trainingValues = useWorkoutProfile
    ? derivedTraining
    : lifestyle;
  const derivedFrequency = Number(
    derivedTraining.trainingFrequency || 0
  );
  const derivedLoadNote =
    derivedTraining.workoutCaloriesPerDay > 0
      ? ` (workouts +${Math.round(
          derivedTraining.workoutCaloriesPerDay
        )} kcal/day)`
      : " (add weight or minutes to increase workout calories)";
  const derivedSummary =
    derivedFrequency > 0
      ? `${derivedTraining.trainingFrequency} days/week - ${
          {
            light: "Light",
            moderate: "Moderate",
            hard: "Hard",
          }[derivedTraining.trainingIntensity] || "--"
        } - ${
          {
            strength: "Strength",
            endurance: "Endurance",
            mixed: "Mixed",
          }[derivedTraining.trainingModality] || "--"
        }${derivedLoadNote}`
      : "Add sessions per week to derive training days.";
  const hasStrengthWorkouts = strengthWorkouts.some(
    (workout) =>
      (workout.exercises || []).some(
        (exercise) => !!exercise.exerciseId
      ) || Number(workout.sessionsPerWeek) > 0
  );

  return (
    <div className="space-y-6">
      <div className="glass-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-display text-[var(--ink)]">
              Basics
            </h3>
            <p className="text-sm text-[var(--muted)]">
              Required to calculate BMR and baseline energy needs.
            </p>
          </div>
          <span className="rounded-full bg-white/70 px-3 py-1 text-xs uppercase tracking-[0.2em] text-[var(--muted)]">
            Required
          </span>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <label className="text-xs text-[var(--muted)]">
            Sex
            <div className="segmented mt-2">
              {[
                { label: "Male", value: "male" },
                { label: "Female", value: "female" },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() =>
                    updateBasics({ sex: option.value })
                  }
                  className={`segmented-button ${
                    basics.sex === option.value ? "is-active" : ""
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </label>

          <label className="text-xs text-[var(--muted)]">
            Age (years)
            <input
              type="number"
              min="0"
              className={`input-field mt-1 ${
                validation.ageValid ? "" : "is-error"
              }`}
              value={basics.age}
              onChange={(event) =>
                updateBasics({ age: event.target.value })
              }
            />
            {!validation.ageValid && basics.age ? (
              <span className="mt-1 block text-[11px] text-[var(--accent)]">
                Enter an age between 13 and 90.
              </span>
            ) : null}
          </label>

          <label className="text-xs text-[var(--muted)]">
            Height
            <div className="mt-2 flex items-center justify-between gap-2">
              <div className="segmented">
                {["cm", "ft"].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => onHeightUnitChange(value)}
                    className={`segmented-button ${
                      basics.heightUnit === value ? "is-active" : ""
                    }`}
                  >
                    {value === "cm" ? "CM" : "FT/IN"}
                  </button>
                ))}
              </div>
            </div>
            {basics.heightUnit === "cm" ? (
              <input
                type="number"
                min="0"
                className={`input-field mt-2 ${
                  validation.heightValid ? "" : "is-error"
                }`}
                value={basics.heightCm}
                onChange={(event) =>
                  updateBasics({ heightCm: event.target.value })
                }
              />
            ) : (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <input
                  type="number"
                  min="0"
                  className="input-field"
                  placeholder="Feet"
                  value={basics.heightFt}
                  onChange={(event) =>
                    updateBasics({ heightFt: event.target.value })
                  }
                />
                <input
                  type="number"
                  min="0"
                  className="input-field"
                  placeholder="Inches"
                  value={basics.heightIn}
                  onChange={(event) =>
                    updateBasics({ heightIn: event.target.value })
                  }
                />
              </div>
            )}
            {!validation.heightValid &&
            (basics.heightCm ||
              basics.heightFt ||
              basics.heightIn) ? (
              <span className="mt-1 block text-[11px] text-[var(--accent)]">
                Height should be between 120 and 230 cm.
              </span>
            ) : null}
          </label>

          <label className="text-xs text-[var(--muted)]">
            Weight
            <div className="mt-2 flex items-center justify-between gap-2">
              <div className="segmented">
                {["kg", "lb"].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => onWeightUnitChange(value)}
                    className={`segmented-button ${
                      basics.weightUnit === value ? "is-active" : ""
                    }`}
                  >
                    {value.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <input
              type="number"
              min="0"
              className={`input-field mt-2 ${
                validation.weightValid ? "" : "is-error"
              }`}
              value={basics.weight}
              onChange={(event) =>
                updateBasics({ weight: event.target.value })
              }
            />
            {!validation.weightValid && basics.weight ? (
              <span className="mt-1 block text-[11px] text-[var(--accent)]">
                Weight should be between 35 and 200 kg.
              </span>
            ) : null}
          </label>

          <label className="text-xs text-[var(--muted)]">
            Body fat % (optional)
            <input
              type="number"
              min="0"
              max="70"
              className={`input-field mt-2 ${
                validation.bodyFatValid ? "" : "is-error"
              }`}
              placeholder="e.g. 18"
              value={basics.bodyFatPct}
              onChange={(event) =>
                updateBasics({ bodyFatPct: event.target.value })
              }
            />
            {!validation.bodyFatValid && basics.bodyFatPct ? (
              <span className="mt-1 block text-[11px] text-[var(--accent)]">
                Enter a body fat percent between 3 and 60.
              </span>
            ) : (
              <span className="mt-1 block text-[11px] text-[var(--muted)]">
                Uses lean mass to refine BMR and protein target.
              </span>
            )}
          </label>
        </div>
      </div>

      <div className="glass-card p-6">
        <div>
          <h3 className="text-lg font-display text-[var(--ink)]">
            Lifestyle inputs
          </h3>
          <p className="text-sm text-[var(--muted)]">
            The more context you provide, the higher the confidence.
          </p>
        </div>
        <div className="mt-4 rounded-2xl border border-white/70 bg-white/70 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)]">
                Training source
              </p>
              <p className="mt-1 text-sm text-[var(--muted)]">
                Use your weekly exercise + cardio log to drive training
                days, intensity, and calorie adjustments.
              </p>
            </div>
            <div className="segmented">
              {[
                { label: "Manual", value: false },
                { label: "Workout-based", value: true },
              ].map((option) => (
                <button
                  key={option.label}
                  type="button"
                  onClick={() => setUseWorkoutProfile(option.value)}
                  className={`segmented-button ${
                    useWorkoutProfile === option.value ? "is-active" : ""
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          {useWorkoutProfile ? (
            <p className="mt-3 text-xs text-[var(--muted)]">
              Derived profile: {derivedSummary} Manual selections are
              ignored.
            </p>
          ) : (
            <p className="mt-3 text-xs text-[var(--muted)]">
              Manual selections will override the workout log for
              calories.
            </p>
          )}
          {!useWorkoutProfile &&
          (hasStrengthWorkouts || cardioSessions.length) ? (
            <p className="mt-2 text-xs text-[var(--accent)]">
              Workout details only change accuracy until you switch to
              workout-based.
            </p>
          ) : null}
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <label className="text-xs text-[var(--muted)]">
            Training frequency
            <select
              className="select-field mt-1 disabled:opacity-60"
              value={trainingValues.trainingFrequency}
              disabled={useWorkoutProfile}
              onChange={(event) =>
                updateLifestyle({
                  trainingFrequency: event.target.value,
                })
              }
            >
              <option value="">Select days</option>
              {frequencyOptions.map((value) => (
                <option key={value} value={value}>
                  {value} days / week
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs text-[var(--muted)]">
            Training intensity
            <select
              className="select-field mt-1 disabled:opacity-60"
              value={trainingValues.trainingIntensity}
              disabled={useWorkoutProfile}
              onChange={(event) =>
                updateLifestyle({
                  trainingIntensity: event.target.value,
                })
              }
            >
              <option value="">Select intensity</option>
              <option value="light">Light</option>
              <option value="moderate">Moderate</option>
              <option value="hard">Hard</option>
            </select>
          </label>

          <label className="text-xs text-[var(--muted)]">
            Training modality
            <select
              className="select-field mt-1 disabled:opacity-60"
              value={trainingValues.trainingModality}
              disabled={useWorkoutProfile}
              onChange={(event) =>
                updateLifestyle({
                  trainingModality: event.target.value,
                })
              }
            >
              <option value="">Select modality</option>
              <option value="strength">Strength</option>
              <option value="endurance">Endurance</option>
              <option value="mixed">Mixed</option>
            </select>
          </label>

          <label className="text-xs text-[var(--muted)]">
            Job activity
            <select
              className="select-field mt-1"
              value={lifestyle.jobActivity}
              onChange={(event) =>
                updateLifestyle({
                  jobActivity: event.target.value,
                })
              }
            >
              <option value="">Select activity</option>
              <option value="desk">Desk</option>
              <option value="standing">Standing & walking</option>
              <option value="manual">Manual labor</option>
            </select>
          </label>

          <label className="text-xs text-[var(--muted)]">
            Daily steps (optional)
            <input
              type="number"
              min="0"
              className="input-field mt-1"
              placeholder="e.g. 8000"
              value={lifestyle.dailySteps}
              onChange={(event) =>
                updateLifestyle({
                  dailySteps: event.target.value,
                })
              }
            />
            <span className="mt-1 block text-[11px] text-[var(--muted)]">
              Adds a small NEAT adjustment to calories.
            </span>
          </label>

          <label className="text-xs text-[var(--muted)]">
            Sleep quality
            <select
              className="select-field mt-1"
              value={lifestyle.sleepQuality}
              onChange={(event) =>
                updateLifestyle({
                  sleepQuality: event.target.value,
                })
              }
            >
              <option value="">Select quality</option>
              <option value="poor">Poor</option>
              <option value="ok">OK</option>
              <option value="good">Good</option>
            </select>
          </label>

          <label className="text-xs text-[var(--muted)]">
            Diet history
            <select
              className="select-field mt-1"
              value={lifestyle.dietHistory}
              onChange={(event) =>
                updateLifestyle({
                  dietHistory: event.target.value,
                })
              }
            >
              <option value="">Select history</option>
              <option value="cutting">Cutting</option>
              <option value="maintenance">Maintenance</option>
              <option value="bulking">Bulking</option>
            </select>
          </label>

          <label className="text-xs text-[var(--muted)]">
            Goal
            <select
              className="select-field mt-1"
              value={lifestyle.goal}
              onChange={(event) =>
                updateLifestyle({ goal: event.target.value })
              }
            >
              <option value="">Select goal</option>
              <option value="fat_loss">Fat loss</option>
              <option value="performance">Performance</option>
              <option value="recomp">Recomposition</option>
            </select>
          </label>
        </div>

      </div>

      {useWorkoutProfile ? (
        <>
          <ExerciseList
            workouts={strengthWorkouts}
            setWorkouts={setStrengthWorkouts}
            unit={strengthUnit}
            onUnitChange={onStrengthUnitChange}
          />

          <CardioList
            sessions={cardioSessions}
            setSessions={setCardioSessions}
          />

          <SportsList
            sessions={sportsSessions}
            setSessions={setSportsSessions}
          />
        </>
      ) : null}
    </div>
  );
}
